import _asyncToGenerator from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/regenerator";
import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { SpeakingMaxAttempts, TimeUnit } from '@module/form';
import { createSpeakingMediaRecorder, isSpeakingRecorderSupported, SpeakingRecorderState, resolveSpeakingLabel, stopSpeakingMediaStream } from '../../common';
export var useSpeakingQuestionPreview = function useSpeakingQuestionPreview(_ref) {
  var settings = _ref.settings,
      label = _ref.label,
      actions = _ref.actions,
      _ref$isDisabled = _ref.isDisabled,
      isDisabled = _ref$isDisabled === void 0 ? false : _ref$isDisabled;
  var prepTimeSec = TimeUnit.toTotalSeconds(settings.preparationTime, settings.preparationTimeUnit);
  var speakingTimeSec = TimeUnit.toTotalSeconds(settings.speakingTime, settings.speakingTimeUnit);
  var minDurationSec = settings.minAnswerDuration === -1 ? 0 : TimeUnit.toTotalSeconds(settings.minAnswerDuration, settings.minAnswerDurationUnit);
  var maxAttempts = settings.maxAttempts; // Keep this aligned with SpeakingAnswer.attemptCount: it counts attempts already used.

  var _useState = useState(0),
      currentAttempts = _useState[0],
      setCurrentAttempts = _useState[1];

  var _useState2 = useState(SpeakingRecorderState.Init),
      recorderState = _useState2[0],
      setRecorderState = _useState2[1];

  var _useState3 = useState(prepTimeSec),
      prepTimeLeft = _useState3[0],
      setPrepTimeSecLeft = _useState3[1];

  var _useState4 = useState(speakingTimeSec),
      speakingTimeLeft = _useState4[0],
      setSpeakingTimeLeft = _useState4[1];

  var _useState5 = useState(0),
      recordedDuration = _useState5[0],
      setRecordedDuration = _useState5[1];

  var _useState6 = useState(null),
      audioUrl = _useState6[0],
      setAudioUrl = _useState6[1];

  var mediaRecorderRef = useRef(null);
  var mediaStreamRef = useRef(null);
  var audioChunksRef = useRef([]);
  var timerRef = useRef(null);
  var recordedDurationRef = useRef(0);
  var audioUrlRef = useRef(null);
  var clearTimer = useCallback(function () {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  var updateAudioUrl = useCallback(function (newUrl) {
    if (audioUrlRef.current && audioUrlRef.current !== newUrl) {
      URL.revokeObjectURL(audioUrlRef.current);
    }

    audioUrlRef.current = newUrl;
    setAudioUrl(newUrl);
  }, []);
  var stopRecording = useCallback(function () {
    clearTimer();

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, [clearTimer]);
  var startRecording = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var stream, recorder, elapsed;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            clearTimer();
            setSpeakingTimeLeft(speakingTimeSec);
            recordedDurationRef.current = 0;
            setRecordedDuration(0);

            if (isSpeakingRecorderSupported()) {
              _context.next = 7;
              break;
            }

            setRecorderState(SpeakingRecorderState.Unsupported);
            return _context.abrupt("return");

          case 7:
            _context.prev = 7;
            _context.next = 10;
            return navigator.mediaDevices.getUserMedia({
              audio: true
            });

          case 10:
            stream = _context.sent;
            mediaStreamRef.current = stream;
            audioChunksRef.current = [];
            recorder = createSpeakingMediaRecorder(stream);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = function (event) {
              if (event.data.size > 0) audioChunksRef.current.push(event.data);
            };

            recorder.onstop = function () {
              var _audioChunksRef$curre, _actions$onRecordComp;

              var mimeType = recorder.mimeType || ((_audioChunksRef$curre = audioChunksRef.current[0]) === null || _audioChunksRef$curre === void 0 ? void 0 : _audioChunksRef$curre.type);
              var audioBlob = new Blob(audioChunksRef.current, mimeType ? {
                type: mimeType
              } : undefined);
              updateAudioUrl(URL.createObjectURL(audioBlob));
              actions === null || actions === void 0 ? void 0 : (_actions$onRecordComp = actions.onRecordComplete) === null || _actions$onRecordComp === void 0 ? void 0 : _actions$onRecordComp.call(actions, audioBlob, recordedDurationRef.current);
              stopSpeakingMediaStream(mediaStreamRef.current);
              mediaStreamRef.current = null;
              setRecorderState(SpeakingRecorderState.Recorded);
            };

            recorder.start();
            setRecorderState(SpeakingRecorderState.Recording);
            _context.next = 25;
            break;

          case 21:
            _context.prev = 21;
            _context.t0 = _context["catch"](7);
            setRecorderState(SpeakingRecorderState.PermissionDenied);
            return _context.abrupt("return");

          case 25:
            elapsed = 0;
            timerRef.current = setInterval(function () {
              elapsed += 1;
              var left = speakingTimeSec - elapsed;
              setSpeakingTimeLeft(Math.max(0, left));
              recordedDurationRef.current = elapsed;
              setRecordedDuration(elapsed);

              if (left <= 0) {
                stopRecording();
              }
            }, 1000);

          case 27:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[7, 21]]);
  })), [speakingTimeSec, stopRecording, actions, clearTimer, updateAudioUrl]);
  var startPreparation = useCallback(function () {
    if (prepTimeSec <= 0) {
      startRecording();
      return;
    }

    setRecorderState(SpeakingRecorderState.Preparation);
    setPrepTimeSecLeft(prepTimeSec);
    var left = prepTimeSec;
    timerRef.current = setInterval(function () {
      left -= 1;
      setPrepTimeSecLeft(left);

      if (left <= 0) {
        clearTimer();
        startRecording();
      }
    }, 1000);
  }, [prepTimeSec, startRecording, clearTimer]);
  var requestMicPermission = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var stream;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (!(isDisabled || !SpeakingMaxAttempts.isUnlimited(maxAttempts) && currentAttempts >= maxAttempts)) {
              _context2.next = 2;
              break;
            }

            return _context2.abrupt("return");

          case 2:
            setRecorderState(SpeakingRecorderState.Permission);

            if (isSpeakingRecorderSupported()) {
              _context2.next = 6;
              break;
            }

            setRecorderState(SpeakingRecorderState.Unsupported);
            return _context2.abrupt("return");

          case 6:
            _context2.prev = 6;
            _context2.next = 9;
            return navigator.mediaDevices.getUserMedia({
              audio: true
            });

          case 9:
            stream = _context2.sent;
            stopSpeakingMediaStream(stream);
            setCurrentAttempts(function (attempts) {
              return attempts + 1;
            });
            startPreparation();
            _context2.next = 18;
            break;

          case 15:
            _context2.prev = 15;
            _context2.t0 = _context2["catch"](6);
            setRecorderState(SpeakingRecorderState.PermissionDenied);

          case 18:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[6, 15]]);
  })), [currentAttempts, isDisabled, maxAttempts, startPreparation]);

  var handleMicClick = function handleMicClick() {
    if (isDisabled || !SpeakingMaxAttempts.isUnlimited(maxAttempts) && currentAttempts >= maxAttempts) return;
    requestMicPermission();
  };

  var handleSkipPreparation = function handleSkipPreparation() {
    if (isDisabled) return;
    clearTimer();
    startRecording();
  };

  var handleReRecord = function handleReRecord() {
    if (isDisabled || !SpeakingMaxAttempts.isUnlimited(maxAttempts) && currentAttempts >= maxAttempts) return;
    setRecorderState(SpeakingRecorderState.Init);
    recordedDurationRef.current = 0;
    setRecordedDuration(0);
    updateAudioUrl(null);
  };

  var handleSubmitClick = function handleSubmitClick() {
    if (isDisabled) return;
    var resolvedLabel = resolveSpeakingLabel(label);
    Modal.confirm({
      title: resolvedLabel.confirmSubmitTitle,
      content: resolvedLabel.confirmSubmitMessage,
      okText: resolvedLabel.submitAnswer,
      cancelText: resolvedLabel.cancel,
      okButtonProps: {
        type: 'primary'
      },
      onOk: function onOk() {
        var _actions$onSubmitSpea;

        setRecorderState(SpeakingRecorderState.Submitted);
        actions === null || actions === void 0 ? void 0 : (_actions$onSubmitSpea = actions.onSubmitSpeakingAnswer) === null || _actions$onSubmitSpea === void 0 ? void 0 : _actions$onSubmitSpea.call(actions);
      }
    });
  };

  useEffect(function () {
    return function () {
      clearTimer();

      if (mediaStreamRef.current) {
        stopSpeakingMediaStream(mediaStreamRef.current);
      }

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, [clearTimer]);
  return {
    recorderState: recorderState,
    currentAttempts: currentAttempts,
    maxAttempts: maxAttempts,
    isUnlimitedAttempts: SpeakingMaxAttempts.isUnlimited(maxAttempts),
    prepTimeSec: prepTimeSec,
    speakingTimeSec: speakingTimeSec,
    minDurationSec: minDurationSec,
    prepTimeLeft: prepTimeLeft,
    speakingTimeLeft: speakingTimeLeft,
    recordedDuration: recordedDuration,
    audioUrl: audioUrl,
    isDisabled: isDisabled,
    handleMicClick: handleMicClick,
    handleSkipPreparation: handleSkipPreparation,
    handleReRecord: handleReRecord,
    handleSubmitClick: handleSubmitClick,
    requestMicPermission: requestMicPermission,
    stopRecording: stopRecording
  };
};